import ApartmentIcon from "@mui/icons-material/Apartment";
import ComputerIcon from "@mui/icons-material/Computer";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DescriptionIcon from "@mui/icons-material/Description";
import HandymanIcon from "@mui/icons-material/Handyman";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import React from "react";

import {
  SortByType,
  SortDirectionEnum,
  AccountQualityEnum,
  TicketCategoryEnum,
  TicketHeadingCategoryEnum,
  TicketSourceEnum,
  TicketActivityEnum,
  TicketStatusEnum,
} from "src/graphql/generated/types";

export const DEFAULT_TICKETS_SORT: Array<SortByType> = [
  { key: "isRead", direction: SortDirectionEnum.Asc },
  { key: "updatedAt", direction: SortDirectionEnum.Desc },
];

interface TicketCategoryQualityDetails {
  quality: AccountQualityEnum;
  label: string;
  order: number;
  Icon: React.ElementType;
  isHeadingCategoryActive: boolean;
}

export const TicketHeadingCategory: Record<
  TicketHeadingCategoryEnum,
  TicketCategoryQualityDetails
> = {
  // TENANT
  [TicketHeadingCategoryEnum.TenantPayment]: {
    quality: AccountQualityEnum.Tenant,
    label: "Paiements, charges, dépôt de garantie",
    order: 1,
    Icon: CreditCardIcon,
    isHeadingCategoryActive: true,
  },
  [TicketHeadingCategoryEnum.TenantRepair]: {
    quality: AccountQualityEnum.Tenant,
    label: "Dépannage, travaux, sinistres",
    order: 2,
    Icon: HandymanIcon,
    isHeadingCategoryActive: true,
  },
  [TicketHeadingCategoryEnum.TenantDocuments]: {
    quality: AccountQualityEnum.Tenant,
    label: "Attestations, coordonnées, documents",
    order: 3,
    Icon: DescriptionIcon,
    isHeadingCategoryActive: true,
  },
  [TicketHeadingCategoryEnum.TenantBuilding]: {
    quality: AccountQualityEnum.Tenant,
    label: "Accès et entretien de l'immeuble",
    order: 4,
    Icon: ApartmentIcon,
    isHeadingCategoryActive: true,
  },
  [TicketHeadingCategoryEnum.TenantCustomerPortal]: {
    quality: AccountQualityEnum.Tenant,
    label: "Espace client",
    order: 5,
    Icon: ComputerIcon,
    isHeadingCategoryActive: true,
  },
  [TicketHeadingCategoryEnum.TenantDeparture]: {
    quality: AccountQualityEnum.Tenant,
    label: "Départ du logement",
    order: 999,
    Icon: MeetingRoomIcon,
    isHeadingCategoryActive: false,
  },

  // LANLORD
  [TicketHeadingCategoryEnum.LandlordIncome]: {
    quality: AccountQualityEnum.Landlord,
    label: "Revenus locatifs, charges, taxes",
    order: 1,
    Icon: CreditCardIcon,
    isHeadingCategoryActive: true,
  },
  [TicketHeadingCategoryEnum.LandlordDocuments]: {
    quality: AccountQualityEnum.Landlord,
    label: "Coordonnées, documents et vente",
    order: 2,
    Icon: DescriptionIcon,
    isHeadingCategoryActive: true,
  },
  [TicketHeadingCategoryEnum.LandlordCustomerPortal]: {
    quality: AccountQualityEnum.Landlord,
    label: "Espace client",
    order: 3,
    Icon: ComputerIcon,
    isHeadingCategoryActive: true,
  },
  [TicketHeadingCategoryEnum.LandlordRepair]: {
    quality: AccountQualityEnum.Landlord,
    label: "Dépannage, travaux, sinistres",
    order: 4,
    Icon: HandymanIcon,
    isHeadingCategoryActive: true,
  },
  [TicketHeadingCategoryEnum.LandlordTenant]: {
    quality: AccountQualityEnum.Landlord,
    label: "Suivi de la location de mon bien",
    order: 5,
    Icon: MeetingRoomIcon,
    isHeadingCategoryActive: true,
  },
  [TicketHeadingCategoryEnum.LandlordCopro]: {
    quality: AccountQualityEnum.Landlord,
    label: "Informations copropriété",
    order: 6,
    Icon: ApartmentIcon,
    isHeadingCategoryActive: true,
  },

  // CO_OWNER
  [TicketHeadingCategoryEnum.CoOwnerPayment]: {
    quality: AccountQualityEnum.CoOwner,
    label: "Paiements, charges et déclarations",
    order: 1,
    Icon: CreditCardIcon,
    isHeadingCategoryActive: true,
  },
  [TicketHeadingCategoryEnum.CoOwnerCustomerPortal]: {
    quality: AccountQualityEnum.CoOwner,
    label: "Espace client",
    order: 2,
    Icon: ComputerIcon,
    isHeadingCategoryActive: true,
  },
  [TicketHeadingCategoryEnum.CoOwnerRepair]: {
    quality: AccountQualityEnum.CoOwner,
    label: "Dépannage, travaux, sinistres",
    order: 3,
    Icon: HandymanIcon,
    isHeadingCategoryActive: true,
  },
  [TicketHeadingCategoryEnum.CoOwnerDocuments]: {
    quality: AccountQualityEnum.CoOwner,
    label: "Coordonnées, documents et vente",
    order: 4,
    Icon: DescriptionIcon,
    isHeadingCategoryActive: true,
  },
  [TicketHeadingCategoryEnum.CoOwnerBuildings]: {
    quality: AccountQualityEnum.CoOwner,
    label: "Accès, entretien et vie de l'immeuble",
    order: 5,
    Icon: ApartmentIcon,
    isHeadingCategoryActive: true,
  },
  [TicketHeadingCategoryEnum.CoOwnerAg]: {
    quality: AccountQualityEnum.CoOwner,
    label: "Assemblées générales et conseil syndical",
    order: 6,
    Icon: HowToVoteIcon,
    isHeadingCategoryActive: true,
  },
};

interface TicketCategoryDetails {
  headingCategory?: TicketHeadingCategoryEnum;
  label: string;
  order: number;
}

export const TicketCategory: Record<TicketCategoryEnum, TicketCategoryDetails> = {
  // TicketHeadingCategoryEnum.TenantPayment
  [TicketCategoryEnum.TenantPaymentRent]: {
    headingCategory: TicketHeadingCategoryEnum.TenantPayment,
    label: "Paiement du loyer",
    order: 1,
  },
  [TicketCategoryEnum.TenantPaymentReceipt]: {
    headingCategory: TicketHeadingCategoryEnum.TenantPayment,
    label: "Informations sur la quittance, l'avis d'échéance",
    order: 2,
  },
  [TicketCategoryEnum.TenantPaymentLatepayment]: {
    headingCategory: TicketHeadingCategoryEnum.TenantPayment,
    label: "Retard de paiement",
    order: 3,
  },
  [TicketCategoryEnum.TenantPaymentDeposit]: {
    headingCategory: TicketHeadingCategoryEnum.TenantPayment,
    label: "Dépôt de garantie",
    order: 4,
  },
  [TicketCategoryEnum.TenantPaymentRegularization]: {
    headingCategory: TicketHeadingCategoryEnum.TenantPayment,
    label: "Régularisation de charges",
    order: 5,
  },
  [TicketCategoryEnum.TenantPaymentAccount]: {
    headingCategory: TicketHeadingCategoryEnum.TenantPayment,
    label: "Situation de mon compte",
    order: 6,
  },
  [TicketCategoryEnum.TenantPaymentOther]: {
    headingCategory: TicketHeadingCategoryEnum.TenantPayment,
    label: "Autre",
    order: 7,
  },

  // TicketHeadingCategoryEnum.TenantRepair
  [TicketCategoryEnum.TenantRepairIntervention]: {
    headingCategory: TicketHeadingCategoryEnum.TenantRepair,
    label: "Demande et suivi d'intervention pour dépannage",
    order: 1,
  },
  [TicketCategoryEnum.TenantRepairWork]: {
    headingCategory: TicketHeadingCategoryEnum.TenantRepair,
    label: "Suivi des travaux",
    order: 2,
  },
  [TicketCategoryEnum.TenantRepairSinister]: {
    headingCategory: TicketHeadingCategoryEnum.TenantRepair,
    label: "Déclaration et suivi de sinistre",
    order: 3,
  },
  [TicketCategoryEnum.TenantRepairOther]: {
    headingCategory: TicketHeadingCategoryEnum.TenantRepair,
    label: "Autre",
    order: 4,
  },

  // TicketHeadingCategoryEnum.TenantDeparture
  [TicketCategoryEnum.TenantDeparture]: {
    headingCategory: TicketHeadingCategoryEnum.TenantDeparture,
    label: "",
    order: 1,
  },

  // TicketHeadingCategoryEnum.TenantDocuments
  [TicketCategoryEnum.TenantDocumentsContacts]: {
    headingCategory: TicketHeadingCategoryEnum.TenantDocuments,
    label: "Modification des coordonnées",
    order: 1,
  },
  [TicketCategoryEnum.TenantDocumentsInsurance]: {
    headingCategory: TicketHeadingCategoryEnum.TenantDocuments,
    label: "Attestation d'assurance",
    order: 2,
  },
  [TicketCategoryEnum.TenantDocumentsDemand]: {
    headingCategory: TicketHeadingCategoryEnum.TenantDocuments,
    label: "Demande de document",
    order: 3,
  },
  [TicketCategoryEnum.TenantDocumentsOther]: {
    headingCategory: TicketHeadingCategoryEnum.TenantDocuments,
    label: "Autre",
    order: 4,
  },

  // TicketHeadingCategoryEnum.TenantBuilding
  [TicketCategoryEnum.TenantBuildingAccess]: {
    headingCategory: TicketHeadingCategoryEnum.TenantBuilding,
    label: "Accès à l'immeuble (badge, digicode...)",
    order: 1,
  },
  [TicketCategoryEnum.TenantBuildingMaintenance]: {
    headingCategory: TicketHeadingCategoryEnum.TenantBuilding,
    label: "Entretien de l'immeuble",
    order: 2,
  },
  [TicketCategoryEnum.TenantBuildingOther]: {
    headingCategory: TicketHeadingCategoryEnum.TenantBuilding,
    label: "Autre",
    order: 3,
  },

  // TicketHeadingCategoryEnum.TenantCustomerPortal
  [TicketCategoryEnum.TenantCustomerPortalPayment]: {
    headingCategory: TicketHeadingCategoryEnum.TenantCustomerPortal,
    label: "Comment payer sur l’espace client",
    order: 1,
  },
  [TicketCategoryEnum.TenantCustomerPortalHelp]: {
    headingCategory: TicketHeadingCategoryEnum.TenantCustomerPortal,
    label: "Aide sur l’espace client",
    order: 2,
  },
  [TicketCategoryEnum.TenantCustomerPortalOther]: {
    headingCategory: TicketHeadingCategoryEnum.TenantCustomerPortal,
    label: "Autre",
    order: 3,
  },

  // TicketHeadingCategoryEnum.LandlordIncome
  [TicketCategoryEnum.LandlordIncomeDeclaration]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordIncome,
    label: "Déclaration des revenus fonciers",
    order: 1,
  },
  [TicketCategoryEnum.LandlordIncomePayment]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordIncome,
    label: "Paiement des factures et des charges de copropriété",
    order: 2,
  },
  [TicketCategoryEnum.LandlordIncomeCrg]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordIncome,
    label: "Information sur le compte rendu de gestion (CRG)",
    order: 3,
  },
  [TicketCategoryEnum.LandlordIncomeTax]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordIncome,
    label: "Taxes foncière, ordures ménagères",
    order: 4,
  },
  [TicketCategoryEnum.LandlordIncomeFees]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordIncome,
    label: "Information sur les honoraires",
    order: 5,
  },
  [TicketCategoryEnum.LandlordIncomeTenant]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordIncome,
    label: "Situation du compte locataire",
    order: 6,
  },
  [TicketCategoryEnum.LandlordIncomeAccount]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordIncome,
    label: "Situation de mon compte",
    order: 7,
  },
  [TicketCategoryEnum.LandlordIncomeCompensation]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordIncome,
    label: "Indemnisation garantie loyer impayé",
    order: 8,
  },
  [TicketCategoryEnum.LandlordIncomeOther]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordIncome,
    label: "Autre",
    order: 9,
  },

  // TicketHeadingCategoryEnum.LandlordDocuments
  [TicketCategoryEnum.LandlordDocumentsContacts]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordDocuments,
    label: "Modification des coordonnées",
    order: 1,
  },
  [TicketCategoryEnum.LandlordDocumentsDemand]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordDocuments,
    label: "Demande de document",
    order: 2,
  },
  [TicketCategoryEnum.LandlordDocumentsEstimate]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordDocuments,
    label: "Demande d'estimation de mon bien",
    order: 3,
  },
  [TicketCategoryEnum.LandlordDocumentsSale]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordDocuments,
    label: "Information vente de mon bien",
    order: 4,
  },
  [TicketCategoryEnum.LandlordDocumentsOther]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordDocuments,
    label: "Autre",
    order: 5,
  },

  // TicketHeadingCategoryEnum.LandlordCustomerPortal
  [TicketCategoryEnum.LandlordCustomerPortal]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordCustomerPortal,
    label: "",
    order: 1,
  },

  // TicketHeadingCategoryEnum.LandlordRepair
  [TicketCategoryEnum.LandlordRepairIntervention]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordRepair,
    label: "Demande et suivi d'intervention pour dépannage",
    order: 1,
  },
  [TicketCategoryEnum.LandlordRepairWork]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordRepair,
    label: "Suivi des travaux",
    order: 2,
  },
  [TicketCategoryEnum.LandlordRepairSinister]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordRepair,
    label: "Déclaration et suivi de sinistre",
    order: 3,
  },
  [TicketCategoryEnum.LandlordRepairOther]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordRepair,
    label: "Autre",
    order: 4,
  },

  // TicketHeadingCategoryEnum.LandlordTenant
  [TicketCategoryEnum.LandlordTenant]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordTenant,
    label: "",
    order: 1,
  },

  // TicketHeadingCategoryEnum.LandlordCopro
  [TicketCategoryEnum.LandlordCoproSyndic]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordCopro,
    label: "Echanges avec le syndic",
    order: 1,
  },
  [TicketCategoryEnum.LandlordCoproAgorganisation]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordCopro,
    label: "Organisation de l'assemblée générale",
    order: 2,
  },
  [TicketCategoryEnum.LandlordCoproAgdecision]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordCopro,
    label: "Décisions d'assemblée générale",
    order: 3,
  },
  [TicketCategoryEnum.LandlordCoproOther]: {
    headingCategory: TicketHeadingCategoryEnum.LandlordCopro,
    label: "Autre",
    order: 4,
  },

  // TicketHeadingCategoryEnum.CoOwnerPayment
  [TicketCategoryEnum.CoOwnerPaymentCharges]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerPayment,
    label: "Paiement de mes charges",
    order: 1,
  },
  [TicketCategoryEnum.CoOwnerPaymentLatepayment]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerPayment,
    label: "Retard de paiement",
    order: 2,
  },
  [TicketCategoryEnum.CoOwnerPaymentExplanations]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerPayment,
    label: "Explications situation de mon compte",
    order: 3,
  },
  [TicketCategoryEnum.CoOwnerPaymentDivision]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerPayment,
    label: "Répartition de charges",
    order: 4,
  },
  [TicketCategoryEnum.CoOwnerPaymentDeclaration]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerPayment,
    label: "Déclaration des revenus fonciers",
    order: 5,
  },
  [TicketCategoryEnum.CoOwnerPaymentOther]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerPayment,
    label: "Autre",
    order: 6,
  },

  // TicketHeadingCategoryEnum.CoOwnerCustomerPortal
  [TicketCategoryEnum.CoOwnerCustomerPortalPayment]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerCustomerPortal,
    label: "Comment payer sur l’espace client",
    order: 1,
  },
  [TicketCategoryEnum.CoOwnerCustomerPortalHelp]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerCustomerPortal,
    label: "Aide sur l'espace client",
    order: 2,
  },
  [TicketCategoryEnum.CoOwnerCustomerPortalOther]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerCustomerPortal,
    label: "Autre",
    order: 3,
  },

  // TicketHeadingCategoryEnum.CoOwnerRepair
  [TicketCategoryEnum.CoOwnerRepairIntervention]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerRepair,
    label: "Demande et suivi d'intervention pour dépannage",
    order: 1,
  },
  [TicketCategoryEnum.CoOwnerRepairWork]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerRepair,
    label: "Suivi des travaux",
    order: 2,
  },
  [TicketCategoryEnum.CoOwnerRepairSinister]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerRepair,
    label: "Déclaration et suivi de sinistre",
    order: 3,
  },
  [TicketCategoryEnum.CoOwnerRepairOther]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerRepair,
    label: "Autre",
    order: 4,
  },

  // TicketHeadingCategoryEnum.CoOwnerDocuments
  [TicketCategoryEnum.CoOwnerDocumentsDemand]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerDocuments,
    label: "Demande de document",
    order: 1,
  },
  [TicketCategoryEnum.CoOwnerDocumentsContact]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerDocuments,
    label: "Modification des coordonnées",
    order: 2,
  },
  [TicketCategoryEnum.CoOwnerDocumentsEstimate]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerDocuments,
    label: "Demande d'estimation de mon bien",
    order: 3,
  },
  [TicketCategoryEnum.CoOwnerDocumentsSale]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerDocuments,
    label: "Information vente de mon bien",
    order: 4,
  },
  [TicketCategoryEnum.CoOwnerDocumentsOther]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerDocuments,
    label: "Autre",
    order: 5,
  },

  // TicketHeadingCategoryEnum.CoOwnerBuildings
  [TicketCategoryEnum.CoOwnerBuildingsAccess]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerBuildings,
    label: "Accès à l'immeuble (badge, digicode...)",
    order: 1,
  },
  [TicketCategoryEnum.CoOwnerBuildingsMaintenance]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerBuildings,
    label: "Entretien de l'immeuble",
    order: 2,
  },
  [TicketCategoryEnum.CoOwnerBuildingsIssue]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerBuildings,
    label: "Problème de voisinage, incivilités",
    order: 3,
  },
  [TicketCategoryEnum.CoOwnerBuildingsOther]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerBuildings,
    label: "Autre",
    order: 4,
  },

  // TicketHeadingCategoryEnum.CoOwnerAg
  [TicketCategoryEnum.CoOwnerAgOrganisation]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerAg,
    label: "Organisation de l'assemblée générale",
    order: 1,
  },
  [TicketCategoryEnum.CoOwnerAgDecision]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerAg,
    label: "Décisions d'assemblée générale",
    order: 2,
  },
  [TicketCategoryEnum.CoOwnerAgContacts]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerAg,
    label: "Contacts du conseil syndical",
    order: 3,
  },
  [TicketCategoryEnum.CoOwnerAgOther]: {
    headingCategory: TicketHeadingCategoryEnum.CoOwnerAg,
    label: "Autre",
    order: 4,
  },

  // TicketCategoryEnum.Unknown
  [TicketCategoryEnum.Unknown]: {
    label: "Autre",
    order: 1,
  },
};
export const ChipTicketStatusColor: { [key in TicketStatusEnum]: string } = {
  [TicketStatusEnum.Open]: "orange.boxBackground",
  [TicketStatusEnum.WorkInProgress]: "cyan.dark",
  [TicketStatusEnum.Processed]: "green.light",
  [TicketStatusEnum.Finished]: "green.light",
};
export const TicketStatusLabel: {
  [Key in TicketStatusEnum]: string;
} = {
  [TicketStatusEnum.Open]: "Enregistrée",
  [TicketStatusEnum.WorkInProgress]: "En cours",
  [TicketStatusEnum.Processed]: "Traitée",
  [TicketStatusEnum.Finished]: "Clôturée",
};

export const TicketSourceLabel: { [key in TicketSourceEnum]: string } = {
  [TicketSourceEnum.Agence]: "Agence",
  [TicketSourceEnum.Email]: "E-mail",
  [TicketSourceEnum.CustomerPortal]: "Espace client",
  [TicketSourceEnum.Telephone]: "Téléphone",
};

export enum TicketResponseStatusEnum {
  All = "Toutes",
  Read = "Lues",
  NotRead = "Non Lues",
}

export const TicketResponseStatusValue: {
  [key in TicketResponseStatusEnum]: boolean | undefined;
} = {
  [TicketResponseStatusEnum.All]: undefined,
  [TicketResponseStatusEnum.Read]: true,
  [TicketResponseStatusEnum.NotRead]: false,
};

export const ProfileInformationHeadingCategory: {
  [key in AccountQualityEnum]: TicketHeadingCategoryEnum;
} = {
  [AccountQualityEnum.Tenant]: TicketHeadingCategoryEnum.TenantDocuments,
  [AccountQualityEnum.CoOwner]: TicketHeadingCategoryEnum.CoOwnerDocuments,
  [AccountQualityEnum.Landlord]: TicketHeadingCategoryEnum.LandlordDocuments,
};

export const TICKET_CREATION_ERROR = "Une erreur est survenue lors de la création de la demande";

export const TicketActivityLabel: Record<TicketActivityEnum, string | null> = {
  [TicketActivityEnum.CategoryChanged]:
    "Pour un meilleur traitement de votre demande, nous avons modifié son motif. L’objet de votre demande est désormais : ",
  [TicketActivityEnum.MissionAttached]: null,
  [TicketActivityEnum.TransferFromAssociate]: null,
  [TicketActivityEnum.TransferFromSupport]:
    "Pour un meilleur traitement de votre demande, celle-ci a été transférée à votre gestionnaire.",
  [TicketActivityEnum.TransferToMiddleOffice]: null,
};

export const UPLOAD_PLACEHOLDER_TEXT = "Ajoutez jusqu'à 5 pièces jointes";
